// ETag-aware GET cache. The last 200 response for a URL (+ Accept) is kept with
// its ETag; the next request sends If-None-Match and a 304 reuses the cached
// body (GitHub does not count 304s against the rate limit).
import { budgetOf, serverOffset, type Budget } from './rateLimit';
import { requestOf, type RequestRecord } from './request';

export interface CachedResponse {
  etag: string;
  body: unknown;
  link: string | null;
  at: number;
}

export interface CacheStore {
  get(key: string): Promise<CachedResponse | undefined>;
  set(key: string, value: CachedResponse): Promise<void>;
}

export type Transport = (url: string, init: RequestInit) => Promise<Response>;

export interface CachedResult<T> {
  status: number;
  body: T;
  link: string | null;
  fromCache: boolean;
  /** The hourly budget the answer's `X-RateLimit-*` headers reported (GitHub sends them on a 304 too); null when it sent none. */
  budget: Budget | null;
}

export class HttpError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
    /** Seconds from the `Retry-After` header, when the response carried one. */
    public retryAfter: number | null = null,
    /** Epoch ms from `X-RateLimit-Reset`, when the response said the limit is used up. */
    public resetAt: number | null = null,
    /** The request the response answered, when known. */
    public request: RequestRecord | null = null,
    /** GitHub's hourly budget as the response's `X-RateLimit-*` headers reported it. */
    public budget: Budget | null = null,
  ) {
    super(message ?? `HTTP ${status}`);
  }
}

/**
 * True when the changelist server itself refused the request for its own
 * rate limit (429 `rate_limited`), as opposed to GitHub's limit passed on
 * through the bridge.
 */
export function isServerRateLimit(status: number, body: unknown): boolean {
  return status === 429 && typeof body === 'object' && body !== null && (body as { error?: unknown }).error === 'rate_limited';
}

/** An HttpError for a failed response, with its rate-limit headers read. */
export function httpError(res: Response, body: unknown, message?: string): HttpError {
  return new HttpError(res.status, body, message, retryAfterSeconds(res), rateLimitResetAt(res.headers), requestOf(res), budgetOf(res.headers));
}

/**
 * When the primary rate limit is used up (`X-RateLimit-Remaining: 0`), the
 * time it resets: `X-RateLimit-Reset` (epoch seconds) in epoch ms, on this
 * device's clock (see `serverOffset`). Else null.
 */
export function rateLimitResetAt(headers: Headers, now = Date.now()): number | null {
  if (headers.get('x-ratelimit-remaining')?.trim() !== '0') return null;
  const reset = Number(headers.get('x-ratelimit-reset'));
  return Number.isFinite(reset) && reset > 0 ? reset * 1000 - serverOffset(headers, now) : null;
}

/** `Retry-After` in seconds (delta-seconds or an HTTP date), or null. */
export function retryAfterSeconds(res: Response): number | null {
  const v = res.headers.get('retry-after');
  if (!v) return null;
  const n = Number(v);
  if (Number.isFinite(n)) return Math.max(0, n);
  const at = Date.parse(v);
  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

export function cacheKey(url: string, accept: string): string {
  return accept + ' ' + url;
}

async function readBody(res: Response, accept: string): Promise<unknown> {
  const type = res.headers.get('content-type') ?? '';
  if (accept.includes('.raw')) return res.text();
  if (type.includes('json')) return res.json();
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function cachedGet<T>(
  url: string,
  opts: { accept?: string; transport: Transport; store: CacheStore; headers?: Record<string, string>; signal?: AbortSignal },
): Promise<CachedResult<T>> {
  const accept = opts.accept ?? 'application/vnd.github+json';
  const key = cacheKey(url, accept);
  const cached = await opts.store.get(key);
  const headers: Record<string, string> = { Accept: accept, ...(opts.headers ?? {}) };
  if (cached?.etag) headers['If-None-Match'] = cached.etag;
  const res = await opts.transport(url, { method: 'GET', headers, credentials: 'same-origin', signal: opts.signal });
  if (res.status === 304 && cached) {
    return { status: 200, body: cached.body as T, link: cached.link, fromCache: true, budget: budgetOf(res.headers) };
  }
  const body = await readBody(res, accept);
  if (!res.ok) throw httpError(res, body);
  const etag = res.headers.get('etag');
  const link = res.headers.get('link');
  if (etag) await opts.store.set(key, { etag, body, link, at: Date.now() });
  return { status: res.status, body: body as T, link, fromCache: false, budget: budgetOf(res.headers) };
}

/** In-memory store, least recently used entries evicted beyond `capacity`. */
export class MemoryStore implements CacheStore {
  map = new Map<string, CachedResponse>();
  constructor(private capacity = Infinity) {}
  clear() { this.map.clear(); }
  async get(key: string) {
    const v = this.map.get(key);
    if (v) {
      this.map.delete(key);
      this.map.set(key, v);
    }
    return v;
  }
  async set(key: string, value: CachedResponse) {
    this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.capacity) this.map.delete(this.map.keys().next().value!);
  }
}

/** A Map bounded to `capacity` entries, evicting the least recently used. */
export class LruMap<K, V> {
  private map = new Map<K, V>();
  constructor(private capacity: number) {}
  get(key: K): V | undefined {
    const v = this.map.get(key);
    if (v !== undefined) {
      this.map.delete(key);
      this.map.set(key, v);
    }
    return v;
  }
  set(key: K, value: V) {
    this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.capacity) this.map.delete(this.map.keys().next().value!);
  }
  delete(key: K) {
    this.map.delete(key);
  }
  clear() { this.map.clear(); }
  get size() {
    return this.map.size;
  }
}

/** Parse a GitHub `Link` header into rel → URL. */
export function parseLink(link: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!link) return out;
  for (const part of link.split(',')) {
    const m = /<([^>]+)>\s*;\s*rel="([^"]+)"/.exec(part.trim());
    if (m) out[m[2]] = m[1];
  }
  return out;
}
